import React, { useEffect, useState } from "react";
import { useDAWStore } from "../../store/dawStore";
import engine from "../../audio/AudioEngine";
import { PianoKeys } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function StepSequencer() {
  const { tracks, regions, selectedRegionId, selectRegion, toggleRegionStep, clearRegionPattern, convertRegionToPianoRoll } = useDAWStore();
  const [playingStep, setPlayingStep] = useState(-1);

  useEffect(() => {
    return engine.onStep((s) => setPlayingStep(s));
  }, []);

  // Only show regions whose track is a drum type AND not flipped to piano-roll view
  const drumRegions = regions.filter((r) => {
    const t = tracks.find((tr) => tr.id === r.trackId);
    return t && ["kick", "snare", "hihat", "clap"].includes(t.type) && t.viewMode !== "pr";
  });

  const active = drumRegions.find((r) => r.id === selectedRegionId) || drumRegions[0];

  if (drumRegions.length === 0) {
    return (
      <div className="h-full flex items-center justify-center" data-testid="step-sequencer">
        <div className="text-center text-white/40 text-sm max-w-md">
          <div>No drum regions on the timeline.</div>
          <div className="label-mono mt-2">
            1. Drag <span className="text-white/80">Kick/Snare/Hi-Hat/Clap</span> from sidebar onto a track header.<br />
            2. Click the <span className="text-white/80">+</span> button on the track to add a MIDI region.<br />
            3. Edit the region's pattern here.
          </div>
        </div>
      </div>
    );
  }

  const track = tracks.find((t) => t.id === active.trackId);

  return (
    <div className="h-full flex flex-col" data-testid="step-sequencer">
      <div className="flex items-center justify-between px-4 py-2 border-b border-[#222]">
        <div className="flex items-center gap-3">
          <h3 className="font-display font-bold text-sm">Step Sequencer</h3>
          <select
            value={active.id}
            onChange={(e) => selectRegion(e.target.value)}
            className="bg-[#0a0a0a] border border-[#222] rounded px-2 py-1 text-[11px] font-mono focus:outline-none focus:border-[#444]"
            data-testid="seq-region-select"
          >
            {drumRegions.map((r) => {
              const trk = tracks.find((tr) => tr.id === r.trackId);
              const barNum = Number.isFinite(Number(r.bar)) ? Number(r.bar) + 1 : "?";
              return (
                <option key={r.id} value={r.id}>{trk?.name} · {r.name} · Bar {barNum}</option>
              );
            })}
          </select>
          <span className="label-mono">editing 1 region · 16 steps</span>
        </div>
        <button
          onClick={() => clearRegionPattern(active.id)}
          className="btn-3d px-3 py-1.5 text-xs"
          data-testid="clear-region-pattern-btn"
        >Clear Region</button>
        <button
          onClick={() => {
            const hits = (active.pattern || []).filter(Boolean).length;
            convertRegionToPianoRoll(active.id);
            toast.success(`Converted ${hits} step${hits === 1 ? "" : "s"} to piano-roll notes · open the Piano Roll tab to edit at 1/64 resolution`);
          }}
          className="btn-3d btn-3d-accent px-3 py-1.5 text-xs font-bold flex items-center gap-1.5 ml-2"
          title="Translate the 16-step pattern into editable MIDI notes you can move off-grid"
          data-testid="convert-to-pr-btn"
        >
          <PianoKeys size={12} weight="bold" /> Convert to Piano Roll
        </button>
      </div>

      <div className="flex-1 overflow-auto p-4">
        <div className="flex gap-2 mb-2 pl-[140px]">
          {Array.from({ length: 16 }, (_, i) => (
            <div key={i}
              className={`flex-1 text-center label-mono ${playingStep === i ? "text-[#00FF41]" : ""}`}
              style={{ minWidth: 38 }}>{i + 1}</div>
          ))}
        </div>

        <div className="flex items-center gap-2 mb-2">
          <div
            className="w-[130px] flex items-center gap-2 px-3 py-2 rounded-md border"
            style={{
              background: "linear-gradient(180deg, #1a1a1a, #141414)",
              borderColor: (track?.color || "#3B82F6") + "60",
            }}
          >
            <span className="w-2 h-2 rounded-full" style={{ background: track?.color, boxShadow: `0 0 6px ${track?.color}` }} />
            <span className="text-xs font-semibold truncate flex-1">{track?.name}</span>
          </div>
          {Array.from({ length: 16 }, (_, step) => (
            <button
              key={step}
              onClick={() => {
                toggleRegionStep(active.id, step);
                if (!active.pattern?.[step]) engine.audition(track);
              }}
              className={`step-cell flex-1 ${active.pattern?.[step] ? "active" : ""} ${playingStep === step ? "playing" : ""} ${step % 4 === 0 ? "beat-marker" : ""}`}
              style={{ "--accent-color": track?.color || "#3B82F6", minWidth: 38 }}
              data-testid={`step-${active.id}-${step}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
